import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ArrowLeft, Send, Check, MapPin, Calculator, Mail, User, Building, Phone, MessageSquare, Video, Clock, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const Request = () => {
  const navigate = useNavigate();
  const [calcData, setCalcData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [expandedVideos, setExpandedVideos] = useState({});

  // Form state
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  // Load calculation from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('revocut_calculation');
    if (stored) {
      setCalcData(JSON.parse(stored));
    }
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare video details for the request
      const videoDetails = calcData?.videos?.map((video, index) => ({
        index: index + 1,
        format: video.format,
        videoType: video.videoType,
        language: video.language,
        subtitleType: video.subtitleType,
        subtitleColor: video.subtitleColor,
        videoCount: video.videoCount,
        minutesPerVideo: video.minutesPerVideo,
        rawMaterialMinutes: video.rawMaterialMinutes,
        needsBRoll: video.needsBRoll,
        rushDelivery: video.rushDelivery,
        extraRevisions: video.extraRevisions,
        notes: video.notes,
        addons: video.addons,
        subtotal: calcData?.calculation?.videoCalcs?.[index]?.subtotal || 0
      })) || [];

      const response = await fetch(`${API_URL}/api/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          team_id: calcData?.teamId,
          team_name: calcData?.team?.name,
          videos: videoDetails,
          total_video_count: calcData?.calculation?.totalVideoCount || videoDetails.reduce((sum, v) => sum + v.videoCount, 0),
          calculated_price: calcData?.calculation?.total,
          netto_price: calcData?.calculation?.nettoTotal,
          margin_percent: calcData?.calculation?.marginPercent,
          margin_amount: calcData?.calculation?.marginAmount,
          base_price: calcData?.basePrice,
        })
      });

      if (response.ok) {
        setSuccess(true);
        sessionStorage.removeItem('revocut_calculation');
      } else {
        const data = await response.json();
        setError(data.detail || 'Ein Fehler ist aufgetreten.');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Success State
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center max-w-md px-6">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}
          >
            <Check className="w-10 h-10" style={{ color: '#5C63FF' }} />
          </div>
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Anfrage gesendet!
          </h1>
          <p className="text-gray-400 mb-8">
            Vielen Dank für deine Anfrage. Wir melden uns innerhalb von 24-48 Stunden bei dir.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div 
          className="blob1 fixed" 
          style={{
            bottom: '10%',
            right: '10%',
            width: '350px',
            height: '350px',
            background: 'radial-gradient(circle, rgba(92, 99, 255, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)'
          }}
        />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 w-full px-6 py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <img src="/revocut-logo.png" alt="RevoCut" className="w-10 h-10" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>REVOCUT</span>
          </button>

          <button 
            onClick={() => navigate('/')}
            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zum Kalkulator
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 px-6 py-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Anfrage senden
            </h1>
            <p className="text-gray-400">
              Überprüfe deine Kalkulation und sende uns deine Projektanfrage.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <User className="w-5 h-5 text-purple-400" />
                  Kontaktdaten
                </h2>

                {error && (
                  <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
                    {error}
                  </div>
                )}

                <div className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Name *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Max Mustermann"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">E-Mail *</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="max@example.com"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Firma (optional)</label>
                    <div className="relative">
                      <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Firma GmbH"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Telefon (optional)</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+49 123 456789"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Nachricht (optional)</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Erzähl uns mehr über dein Projekt..."
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full mt-8 py-4 rounded-xl font-bold text-lg transition-all bg-purple-500 hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-request-btn"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Anfrage absenden
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Right: Summary */}
            <div className="lg:col-span-1">
              <div className="rounded-2xl border border-white/10 p-6 sticky top-6" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <Calculator className="w-5 h-5 text-purple-400" />
                  Dein Projekt
                </h2>

                {calcData ? (
                  <>
                    {/* Team */}
                    {calcData.team && (
                      <div className="flex items-center gap-3 mb-6 pb-6 border-b border-white/10">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)' }}
                        >
                          <MapPin className="w-6 h-6" style={{ color: '#5C63FF' }} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Team</p>
                          <p className="font-semibold">{calcData.team.name}</p>
                        </div>
                      </div>
                    )}

                    {/* Video Summary */}
                    <div className="space-y-3 mb-6">
                      <p className="text-sm text-gray-400 flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        {calcData.videos?.length || 0} Video-Konfiguration{(calcData.videos?.length || 0) !== 1 ? 'en' : ''}
                      </p>
                      
                      {/* Video Details */}
                      {calcData.videos?.map((video, index) => {
                        const isExpanded = expandedVideos[index];
                        const subtotal = calcData.calculation?.videoCalcs?.[index]?.subtotal || 0;
                        
                        return (
                          <div key={video.id || index} className="rounded-xl border border-white/10 overflow-hidden">
                            <button
                              onClick={() => setExpandedVideos(prev => ({ ...prev, [index]: !prev[index] }))}
                              className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#5C63FF' }}>
                                  {index + 1}
                                </div>
                                <span className="text-sm">{video.videoCount}x • {video.minutesPerVideo} Min</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium" style={{ color: '#5C63FF' }}>€{subtotal.toFixed(0)}</span>
                                {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                              </div>
                            </button>
                            
                            {isExpanded && (
                              <div className="p-3 text-xs space-y-1 border-t border-white/5">
                                <div className="flex justify-between"><span className="text-gray-500">Format:</span><span>{video.format}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Typ:</span><span>{video.videoType}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Sprache:</span><span>{video.language}</span></div>
                                {video.subtitleType !== 'none' && (
                                  <div className="flex justify-between"><span className="text-gray-500">Untertitel:</span><span>{video.subtitleType}</span></div>
                                )}
                                {video.needsBRoll && (
                                  <div className="flex justify-between"><span className="text-gray-500">B-Roll:</span><span className="text-green-400">Ja</span></div>
                                )}
                                {video.rushDelivery !== 'none' && (
                                  <div className="flex justify-between"><span className="text-gray-500">Lieferung:</span><span className="text-orange-400">{video.rushDelivery}</span></div>
                                )}
                                {video.notes && (
                                  <div className="pt-1 border-t border-white/5">
                                    <span className="text-gray-500">Notizen:</span>
                                    <p className="text-gray-300 italic mt-1">"{video.notes}"</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Price Breakdown */}
                    <div className="space-y-2 mb-4 pb-4 border-b border-white/10 text-sm">
                      <div className="flex justify-between text-gray-400">
                        <span>Netto</span>
                        <span>€{(calcData.calculation?.nettoTotal || 0).toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Service ({calcData.calculation?.marginPercent || 0}%)</span>
                        <span>+€{(calcData.calculation?.marginAmount || 0).toFixed(0)}</span>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Gesamtpreis</span>
                      <div className="total-price text-2xl font-bold text-white">
                        €{(calcData.calculation?.total || 0).toFixed(0)}
                      </div>
                    </div>

                    <button 
                      onClick={() => navigate(calcData.teamId ? `/calculator/${calcData.teamId}` : '/calculator')}
                      className="btn-secondary w-full mt-4 flex items-center justify-center gap-2"
                    >
                      <Calculator className="w-4 h-4" />
                      Bearbeiten
                    </button>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Calculator className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                    <p className="text-gray-400 mb-4">Keine Kalkulation vorhanden</p>
                    <button 
                      onClick={() => navigate('/calculator')}
                      className="btn-primary"
                    >
                      Zum Kalkulator
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Request;
